import { Text } from '@repletoryreach/sdk-ui';
import { Blank } from '../../components/cells';
import { formatMinor } from '../../../../lib/format';
import '../sku-rates.css';

/** A plan as this cell needs it: the code the amounts are keyed by, and the name an operator knows it by. */
export interface PlanLabel { code: string; name: string }

/**
 * What one unit costs on every plan on sale, as one compact cell: the plan's
 * name and its price, plan after plan. A rate is no longer one figure (owner's
 * ruling, 21 September 2026), and a column per plan would push the table off
 * the screen.
 *
 * Each name and price wrap as a pair, so a narrow cell breaks between plans and
 * never inside one. The order is the price list's. A plan the list does not
 * name (the plans could not be read) falls back to its code, which is what the
 * plans page shows and an operator already quotes.
 */
export function PlanPrices({ amounts, plans, currency, align = 'start' }: {
  /** Minor units keyed by plan code: `plan_amounts_minor` from the service, or a preview of a charge being typed. */
  amounts: Record<string, number> | undefined;
  plans: PlanLabel[];
  currency: string;
  /** `end` for a right-aligned money column. */
  align?: 'start' | 'end';
}) {
  if (!amounts) return <Blank />;
  const named = plans.filter((p) => p.code in amounts);
  const known = new Set(named.map((p) => p.code));
  const entries = [...named, ...Object.keys(amounts).filter((code) => !known.has(code)).map((code) => ({ code, name: code }))];
  if (entries.length === 0) return <Blank />;
  return (
    <span className="rr-skurates-plans" style={{ justifyContent: align === 'end' ? 'flex-end' : 'flex-start' }}>
      {entries.map((p) => (
        <span key={p.code} className="rr-skurates-plan">
          <Text span role="caption" c="var(--rr-color-text-muted)">{p.name}</Text>
          <Text span role="caption" fw={600} className="rr-billing-money">{formatMinor(amounts[p.code] ?? 0, currency)}</Text>
        </span>
      ))}
    </span>
  );
}
